import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { ACTOR_ID_HEADER_KEY } from "constants";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { OLIVEEB_COLORS } from "workspaces/colors";
import {
  fetchWorkspaceInvitePrivileges,
  fetchWorkspaceInviteRoles,
  fetchWorkspaceMembers,
} from "services/workspaceService";
import apiClient from "utils/apiClient";

const { Text } = Typography;

const valueFrom = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "") || "-";

const searchableText = (item) =>
  [
    item?.name,
    item?.code,
    item?.type,
    item?.owner,
    item?.description,
    item?.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const filterItems = (items, query) => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return items;
  return items.filter((item) => searchableText(item).includes(normalized));
};

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || []);

const userName = (user) => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || user?.username || user?.email || user?.id || "User";
};

const userEmail = (user) =>
  user?.email ||
  user?.contactDetails?.personalEmail ||
  user?.contactDetails?.workEmail ||
  user?.personalEmail ||
  "";

const membershipUserId = (membership) =>
  membership?.user?.id || membership?.userId || "";

const isActiveMember = (membership) => {
  const status = String(membership?.membershipStatus || "").toUpperCase();
  return !["PENDING", "CANCELLED", "DECLINED", "REMOVED", "SUSPENDED"].includes(status);
};

const roleIdFrom = (item) => item?.roleId || item?.role?.id || item?.id || "";
const privilegeIdFrom = (item) =>
  item?.privilegeId || item?.privilege?.id || item?.id || "";

const selectOption = (item, label) => ({
  value: item.id,
  label,
  searchText: [
    item?.name,
    item?.code,
    item?.scope,
    item?.type,
    item?.owner,
    item?.description,
    item?.id,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase(),
});

const AccessControl = () => {
  const { user } = useAuth();
  const { selectedWorkspace } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const [roles, setRoles] = useState([]);
  const [privileges, setPrivileges] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [userRoleIds, setUserRoleIds] = useState([]);
  const [userPrivilegeIds, setUserPrivilegeIds] = useState([]);
  const [assignRoleIds, setAssignRoleIds] = useState([]);
  const [assignPrivilegeIds, setAssignPrivilegeIds] = useState([]);
  const [roleAssignSearch, setRoleAssignSearch] = useState("");
  const [privilegeAssignSearch, setPrivilegeAssignSearch] = useState("");

  const loadAccessControl = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const [rolesResult, privilegesResult] = await Promise.all([
        fetchWorkspaceInviteRoles(),
        fetchWorkspaceInvitePrivileges(),
      ]);
      setRoles(Array.isArray(rolesResult) ? rolesResult : []);
      setPrivileges(Array.isArray(privilegesResult) ? privilegesResult : []);
    } catch (error) {
      setLoadError(error?.message || "Failed to load access control data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccessControl();
  }, [loadAccessControl]);

  const loadWorkspaceMembers = useCallback(async () => {
    if (!actorId || !selectedWorkspace?.id) {
      setWorkspaceMembers([]);
      setUserOptions([]);
      setSelectedUser(null);
      return;
    }

    try {
      setUserLoading(true);
      const page = await fetchWorkspaceMembers({
        actorId,
        workspaceId: selectedWorkspace.id,
        size: 100,
      });
      setWorkspaceMembers((page.content || []).filter(isActiveMember));
    } catch (error) {
      message.error(error?.message || "Failed to load workspace members.");
      setWorkspaceMembers([]);
    } finally {
      setUserLoading(false);
    }
  }, [actorId, selectedWorkspace?.id]);

  useEffect(() => {
    setSelectedUser(null);
    setUserSearch("");
    setUserOptions([]);
    setUserRoleIds([]);
    setUserPrivilegeIds([]);
    loadWorkspaceMembers();
  }, [loadWorkspaceMembers]);

  const filteredRoles = useMemo(() => filterItems(roles, query), [query, roles]);
  const filteredPrivileges = useMemo(
    () => filterItems(privileges, query),
    [privileges, query],
  );
  const roleOptions = useMemo(
    () => roles.map((role) => selectOption(role, valueFrom(role.name, role.code, role.id))),
    [roles],
  );
  const privilegeOptions = useMemo(
    () =>
      privileges.map((privilege) =>
        selectOption(privilege, valueFrom(privilege.name, privilege.scope, privilege.code, privilege.id)),
      ),
    [privileges],
  );
  const visibleAssignRoleOptions = roleAssignSearch.trim()
    ? roleOptions
    : roleOptions.filter((option) => assignRoleIds.includes(option.value));
  const visibleAssignPrivilegeOptions = privilegeAssignSearch.trim()
    ? privilegeOptions
    : privilegeOptions.filter((option) => assignPrivilegeIds.includes(option.value));
  const rolesById = useMemo(
    () => Object.fromEntries(roles.map((role) => [role.id, role])),
    [roles],
  );
  const privilegesById = useMemo(
    () => Object.fromEntries(privileges.map((privilege) => [privilege.id, privilege])),
    [privileges],
  );

  const searchUsers = useCallback((value) => {
    const term = String(value || "").trim();
    setUserSearch(value);
    if (term.length < 3) {
      setUserOptions([]);
      return;
    }

    const normalized = term.toLowerCase();
    setUserOptions(
      workspaceMembers
        .map((membership) => membership?.user || { id: membershipUserId(membership) })
        .filter((item) => item?.id)
        .filter((item) =>
          [
            userName(item),
            userEmail(item),
            item?.username,
            item?.id,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(normalized),
        )
        .slice(0, 12)
        .map((item) => ({
          value: item.id,
          label: `${userName(item)}${userEmail(item) ? ` • ${userEmail(item)}` : ""}`,
          user: item,
        })),
    );
  }, [workspaceMembers]);

  const loadUserAssignments = useCallback(async (userId) => {
    if (!userId) return;

    try {
      setAssignmentLoading(true);
      const [roleResponse, privilegeResponse] = await Promise.all([
        apiClient.get(`/identity-service/api/v1/roles/user/${userId}`, {
          params: { code: "OVACUS" },
        }),
        apiClient.get(`/identity-service/api/v1/privileges/user/${userId}`, {
          params: { code: "OVACUS" },
        }),
      ]);

      setUserRoleIds(pageContent(roleResponse.data).map(roleIdFrom).filter(Boolean));
      setUserPrivilegeIds(
        pageContent(privilegeResponse.data).map(privilegeIdFrom).filter(Boolean),
      );
      setAssignRoleIds([]);
      setAssignPrivilegeIds([]);
      setRoleAssignSearch("");
      setPrivilegeAssignSearch("");
    } catch (error) {
      message.error(error?.message || "Failed to load user access.");
    } finally {
      setAssignmentLoading(false);
    }
  }, []);

  const selectUser = (userId, option) => {
    setSelectedUser(option?.user || { id: userId });
    setUserSearch("");
    loadUserAssignments(userId);
  };

  const assignAccess = async () => {
    if (!selectedUser?.id) {
      message.error("Select a user first.");
      return;
    }
    if (!assignRoleIds.length && !assignPrivilegeIds.length) {
      message.error("Select roles or privileges to assign.");
      return;
    }

    try {
      setAssignmentSaving(true);
      const headers = actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};
      await Promise.all([
        assignRoleIds.length
          ? apiClient.put(
              `/identity-service/api/v1/roles/user/${selectedUser.id}/assign`,
              assignRoleIds,
              { headers },
            )
          : Promise.resolve(),
        assignPrivilegeIds.length
          ? apiClient.put(
              `/identity-service/api/v1/privileges/user/${selectedUser.id}/assign`,
              assignPrivilegeIds,
              { headers },
            )
          : Promise.resolve(),
      ]);
      message.success("User access assigned.");
      await loadUserAssignments(selectedUser.id);
    } catch (error) {
      message.error(error?.message || "Failed to assign user access.");
    } finally {
      setAssignmentSaving(false);
    }
  };

  const removeRole = async (roleId) => {
    if (!selectedUser?.id || !roleId) return;
    try {
      const headers = actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};
      await apiClient.put(
        `/identity-service/api/v1/roles/user/${selectedUser.id}/remove`,
        [roleId],
        { headers },
      );
      message.success("Role removed.");
      await loadUserAssignments(selectedUser.id);
    } catch (error) {
      message.error(error?.message || "Failed to remove role.");
    }
  };

  const removePrivilege = async (privilegeId) => {
    if (!selectedUser?.id || !privilegeId) return;
    try {
      const headers = actorId ? { [ACTOR_ID_HEADER_KEY]: actorId } : {};
      await apiClient.delete(
        `/identity-service/api/v1/privileges/user/${selectedUser.id}/remove/${privilegeId}`,
        { headers },
      );
      message.success("Privilege removed.");
      await loadUserAssignments(selectedUser.id);
    } catch (error) {
      message.error(error?.message || "Failed to remove privilege.");
    }
  };

  const roleColumns = [
    {
      title: "Role",
      key: "role",
      render: (_, role) => (
        <Space direction="vertical" size={2}>
          <Text strong>{valueFrom(role.name, role.code, role.id)}</Text>
          {role.description ? <Text type="secondary">{role.description}</Text> : null}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type) => <Tag color="blue">{valueFrom(type, "Role")}</Tag>,
    },
    {
      title: "Owner",
      dataIndex: "owner",
      key: "owner",
      width: 180,
      render: (owner) => <Text>{valueFrom(owner)}</Text>,
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 280,
      render: (id) => <Text copyable={Boolean(id)}>{valueFrom(id)}</Text>,
    },
  ];

  const privilegeColumns = [
    {
      title: "Privilege",
      key: "privilege",
      render: (_, privilege) => (
        <Space direction="vertical" size={2}>
          <Text strong>{valueFrom(privilege.name, privilege.code, privilege.id)}</Text>
          {privilege.description ? (
            <Text type="secondary">{privilege.description}</Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 150,
      render: (type) => <Tag color="green">{valueFrom(type, "Privilege")}</Tag>,
    },
    {
      title: "Owner",
      dataIndex: "owner",
      key: "owner",
      width: 180,
      render: (owner) => <Text>{valueFrom(owner)}</Text>,
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 280,
      render: (id) => <Text copyable={Boolean(id)}>{valueFrom(id)}</Text>,
    },
  ];

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<SafetyCertificateOutlined />}
          title="Access Control"
          subtitle="Assign user roles and privileges, and review available access definitions"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search roles or privileges"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 280, borderRadius: 8 }}
        />
      </div>

      {loadError ? (
        <Alert
          type="warning"
          showIcon
          message="Access control data is unavailable"
          description={loadError}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Card style={{ borderRadius: 8, borderColor: OLIVEEB_COLORS.border }}>
        <Tabs
          defaultActiveKey="users"
          items={[
            {
              key: "users",
              label: (
                <Space size={6}>
                  <UserSwitchOutlined />
                  User Assignments
                </Space>
              ),
              children: (
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <Card
                    size="small"
                    style={{
                      borderRadius: 8,
                      borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
                      background: OLIVEEB_COLORS.bgSoft,
                    }}
                  >
                    <Space direction="vertical" size={12} style={{ width: "100%" }}>
                      <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                        Select User
                      </Text>
                      <Space wrap size={[6, 6]}>
                        <Tag color={selectedWorkspace ? "blue" : "gold"} style={{ marginInlineEnd: 0 }}>
                          {selectedWorkspace?.name || "No workspace selected"}
                        </Tag>
                        {selectedWorkspace ? (
                          <Tag color="green" style={{ marginInlineEnd: 0 }}>
                            {workspaceMembers.length} active members
                          </Tag>
                        ) : null}
                      </Space>
                      <Select
                        showSearch
                        allowClear
                        labelInValue
                        disabled={!selectedWorkspace}
                        value={
                          selectedUser?.id
                            ? {
                                value: selectedUser.id,
                                label: `${userName(selectedUser)}${userEmail(selectedUser) ? ` • ${userEmail(selectedUser)}` : ""}`,
                              }
                            : undefined
                        }
                        placeholder={
                          selectedWorkspace
                            ? "Start typing a workspace member name or email"
                            : "Select a workspace first"
                        }
                        searchValue={userSearch}
                        filterOption={false}
                        onSearch={searchUsers}
                        onSelect={(value, option) => selectUser(value.value, option)}
                        onClear={() => {
                          setSelectedUser(null);
                          setUserSearch("");
                          setUserRoleIds([]);
                          setUserPrivilegeIds([]);
                        }}
                        loading={userLoading}
                        notFoundContent={
                          userSearch.trim().length < 3
                            ? "Type at least 3 characters"
                            : userLoading
                              ? "Loading workspace members..."
                              : "No workspace members found"
                        }
                        options={userOptions}
                        style={{ width: "100%" }}
                      />
                    </Space>
                  </Card>

                  {selectedUser ? (
                    <>
                      <Card
                        title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Assign Access</Text>}
                        extra={
                          <Button
                            icon={<ReloadOutlined />}
                            size="small"
                            onClick={() => loadUserAssignments(selectedUser.id)}
                            loading={assignmentLoading}
                          >
                            Refresh
                          </Button>
                        }
                        style={{ borderRadius: 8 }}
                      >
                        <Space direction="vertical" size={12} style={{ width: "100%" }}>
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={assignRoleIds}
                            options={visibleAssignRoleOptions}
                            placeholder="Search roles to assign"
                            searchValue={roleAssignSearch}
                            autoClearSearchValue
                            onSearch={setRoleAssignSearch}
                            onChange={setAssignRoleIds}
                            onSelect={() => setRoleAssignSearch("")}
                            onClear={() => setRoleAssignSearch("")}
                            onBlur={() => setRoleAssignSearch("")}
                            optionFilterProp="searchText"
                            filterOption={(input, option) =>
                              option?.searchText?.includes(input.toLowerCase())
                            }
                            notFoundContent={
                              roleAssignSearch.trim() ? "No roles found" : "Start typing to search roles"
                            }
                            style={{ width: "100%" }}
                          />
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            value={assignPrivilegeIds}
                            options={visibleAssignPrivilegeOptions}
                            placeholder="Search privileges to assign"
                            searchValue={privilegeAssignSearch}
                            autoClearSearchValue
                            onSearch={setPrivilegeAssignSearch}
                            onChange={setAssignPrivilegeIds}
                            onSelect={() => setPrivilegeAssignSearch("")}
                            onClear={() => setPrivilegeAssignSearch("")}
                            onBlur={() => setPrivilegeAssignSearch("")}
                            optionFilterProp="searchText"
                            filterOption={(input, option) =>
                              option?.searchText?.includes(input.toLowerCase())
                            }
                            notFoundContent={
                              privilegeAssignSearch.trim()
                                ? "No privileges found"
                                : "Start typing to search privileges"
                            }
                            style={{ width: "100%" }}
                          />
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            loading={assignmentSaving}
                            onClick={assignAccess}
                            style={{
                              borderColor: OLIVEEB_COLORS.oliveebGreen,
                              background: OLIVEEB_COLORS.oliveebGreen,
                              fontWeight: 700,
                            }}
                          >
                            Assign Selected
                          </Button>
                        </Space>
                      </Card>

                      <Card
                        title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Current User Access</Text>}
                        style={{ borderRadius: 8 }}
                      >
                        <Space direction="vertical" size={14} style={{ width: "100%" }}>
                          <div>
                            <Text type="secondary">Roles</Text>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                              {userRoleIds.length ? userRoleIds.map((roleId) => {
                                const role = rolesById[roleId];
                                return (
                                  <Tag key={roleId} color="blue" style={{ marginInlineEnd: 0, padding: "3px 6px" }}>
                                    {valueFrom(role?.name, role?.code, roleId)}
                                    <Popconfirm
                                      title="Remove role?"
                                      okText="Remove"
                                      okButtonProps={{ danger: true }}
                                      onConfirm={() => removeRole(roleId)}
                                    >
                                      <Button
                                        danger
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        style={{ marginInlineStart: 4 }}
                                      />
                                    </Popconfirm>
                                  </Tag>
                                );
                              }) : <Text>-</Text>}
                            </div>
                          </div>
                          <div>
                            <Text type="secondary">Privileges</Text>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
                              {userPrivilegeIds.length ? userPrivilegeIds.map((privilegeId) => {
                                const privilege = privilegesById[privilegeId];
                                return (
                                  <Tag key={privilegeId} color="green" style={{ marginInlineEnd: 0, padding: "3px 6px" }}>
                                    {valueFrom(privilege?.name, privilege?.scope, privilegeId)}
                                    <Popconfirm
                                      title="Remove privilege?"
                                      okText="Remove"
                                      okButtonProps={{ danger: true }}
                                      onConfirm={() => removePrivilege(privilegeId)}
                                    >
                                      <Button
                                        danger
                                        type="text"
                                        size="small"
                                        icon={<DeleteOutlined />}
                                        style={{ marginInlineStart: 4 }}
                                      />
                                    </Popconfirm>
                                  </Tag>
                                );
                              }) : <Text>-</Text>}
                            </div>
                          </div>
                        </Space>
                      </Card>
                    </>
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Select a user to view and assign roles or privileges"
                    />
                  )}
                </Space>
              ),
            },
            {
              key: "roles",
              label: (
                <Space size={6}>
                  <TeamOutlined />
                  Roles
                  <Tag color="blue">{filteredRoles.length}</Tag>
                </Space>
              ),
              children: (
                <Table
                  rowKey={(role) => role.id || role.code || role.name}
                  columns={roleColumns}
                  dataSource={filteredRoles}
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  scroll={{ x: 900 }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No roles found"
                      />
                    ),
                  }}
                />
              ),
            },
            {
              key: "privileges",
              label: (
                <Space size={6}>
                  <KeyOutlined />
                  Privileges
                  <Tag color="green">{filteredPrivileges.length}</Tag>
                </Space>
              ),
              children: (
                <Table
                  rowKey={(privilege) => privilege.id || privilege.code || privilege.name}
                  columns={privilegeColumns}
                  dataSource={filteredPrivileges}
                  loading={loading}
                  pagination={{ pageSize: 10, showSizeChanger: false }}
                  scroll={{ x: 900 }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No privileges found"
                      />
                    ),
                  }}
                />
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default AccessControl;
